import React, { useEffect, useRef } from 'react';
import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLocation } from 'react-router-dom';

gsap.registerPlugin(ScrollTrigger);

const SmoothScroll = ({ children }) => {
  const { pathname } = useLocation();
  const lenisRef = useRef(null);
  const isAdminPath = pathname.startsWith('/admin');

  useEffect(() => {
    if (isAdminPath) {
      return;
    }

    // Initialize Lenis
    const lenis = new Lenis({
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 1.1,
      infinite: false,
      prevent: (node) => {
        return node.closest('[data-lenis-prevent]');
      },
    });

    lenisRef.current = lenis;

    // Sync ScrollTrigger
    lenis.on('scroll', ScrollTrigger.update);

    const gsapTicker = (time) => {
      lenis.raf(time * 1000);
    };

    gsap.ticker.add(gsapTicker);
    gsap.ticker.lagSmoothing(0);

    // Observer to stop Lenis when a modal is open (body overflow hidden)
    const observer = new MutationObserver(() => {
      const isLocked = document.body.style.overflow === 'hidden';
      if (isLocked) {
        lenis.stop();
      } else {
        lenis.start();
      }
    });

    observer.observe(document.body, { attributes: true, attributeFilter: ['style'] });

    return () => {
      lenis.destroy();
      gsap.ticker.remove(gsapTicker);
      ScrollTrigger.getAll().forEach(t => t.kill());
      observer.disconnect();
    };

  }, [isAdminPath]);

  // Handle scroll to top on path change smoothly
  useEffect(() => {
    if (isAdminPath) {
      window.scrollTo(0, 0);
      return;
    }
    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, { immediate: true });
    }
  }, [pathname, isAdminPath]);

  return <>{children}</>;
};

export default SmoothScroll;
